/**
 * SyncCoordinatorService
 *
 * Singleton service for cross-tab coordination using BroadcastChannel
 * (with localStorage fallback for unsupported browsers)
 *
 * KEY FEATURES:
 * - Cross-tab change notifications (prevent stale caches)
 * - Export mutex with TTL (prevent concurrent exports and deadlocks)
 * - Granular per-record subscriptions (prevent unnecessary re-renders)
 * - Coalescing and de-duplication (prevent stampedes from burst edits)
 * - Self-bounce guard (prevent tab from notifying itself)
 * - Re-entrancy guard (prevent cascaded message loops)
 * - Graceful degradation (localStorage fallback for Safari < 15.4)
 *
 * SECURITY: Messages contain ONLY metadata (table/recordId/section), NEVER data snapshots
 */

import type {
  SyncBroadcast,
  RecordChangeCallback,
  RecordChangePayload,
  TableChangeCallback,
  TableChangePayload,
} from '@/types/sync/SyncCoordinatorTypes';
import {
  SYNC_CHANNEL,
  SYNC_FALLBACK_KEY,
  SYNC_LOCK_KEY,
  SYNC_LOCK_TTL_MS,
} from './constants';
import { generateUUID } from '@/utils/uuid';

/**
 * Lock state stored in localStorage
 */
interface SyncLock {
  lockId: string;
  originId: string;
  expiresAt: number;
}

class SyncCoordinatorService {
  // Tab identification (sessionStorage UUID - tab-specific, not device-level)
  private originId: string;

  // BroadcastChannel or localStorage fallback
  private channel: BroadcastChannel | null = null;
  private usingFallback: boolean = false;
  private storageListener: ((e: StorageEvent) => void) | null = null;

  // Subscription management
  private recordSubscribers: Map<string, Set<RecordChangeCallback>> = new Map();
  private tableSubscribers: Map<string, Set<TableChangeCallback>> = new Map();
  private reconnectionSubscribers: Set<(message: SyncBroadcast) => void> = new Set();

  // Reconnection tracking (cross-tab coordination)
  private activeReconnections: Map<string, { correlationId: string; startedAt: number }> = new Map();

  // Coalescing (100ms debounce per record)
  private pendingRefetches: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private readonly COALESCE_DELAY_MS = 100;

  // opId de-duplication (5s cache)
  private seenOpIds: Set<string> = new Set();
  private opIdTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private readonly OPID_EXPIRY_MS = 5000;

  // Export mutex coordination
  private lockState: {
    currentLockId: string | null;
    lockOwnerId: string | null;
    lockExpiry: number | null;
  } = {
    currentLockId: null,
    lockOwnerId: null,
    lockExpiry: null,
  };

  // Re-entrancy guard (prevent cascaded message loops)
  private isProcessing: boolean = false;

  constructor() {
    // Generate unique tab ID for this page session
    // NOTE: Do NOT persist in sessionStorage - duplicated tabs would share the same ID
    // and the self-bounce guard would incorrectly block cross-tab messages
    this.originId = generateUUID();

    // Initialize BroadcastChannel (with fallback to localStorage)
    this.initializeChannel();

    // Auto-cleanup on tab close (release lock, close channel)
    window.addEventListener('beforeunload', () => this.cleanup());
  }

  /**
   * Initialize BroadcastChannel or fallback to localStorage
   * CRITICAL: Only one listener path active at a time (never both)
   */
  private initializeChannel(): void {
    try {
      this.channel = new BroadcastChannel(SYNC_CHANNEL);
      this.usingFallback = false;
      this.channel.addEventListener('message', (e) => this.handleMessage(e.data));
      // Success: using BroadcastChannel, do NOT attach storage listener
    } catch {
      // BroadcastChannel unavailable (Safari < 15.4, older browsers)
      console.warn('[SyncCoordinator] BroadcastChannel unavailable, using localStorage fallback');
      this.initializeStorageFallback();
    }
  }

  /**
   * Initialize localStorage fallback for cross-tab communication
   */
  private initializeStorageFallback(): void {
    this.usingFallback = true;
    this.storageListener = (e: StorageEvent) => this.handleStorageEvent(e);
    window.addEventListener('storage', this.storageListener);
  }

  /**
   * Handle message from BroadcastChannel
   * Re-entrancy guard prevents cascaded message loops (critical for system-wide rollout)
   */
  private handleMessage(message: SyncBroadcast): void {
    // Guard against re-entrant cascades (e.g., import → write → tap → emit)
    // Critical when this service is used for ALL Dexie tables (not just wizards)
    if (this.isProcessing) {
      return;
    }

    // Self-bounce guard: ignore own messages
    if (message.originId === this.originId) {
      return;
    }

    try {
      this.isProcessing = true;

      if (message.type === 'record-changed') {
        this.handleRecordChanged(message);
      } else if (message.type === 'export-lock') {
        this.handleExportLock(message);
      } else if (message.type === 'reconnect:start') {
        this.handleReconnectionStart(message);
      } else if (message.type === 'reconnect:end') {
        this.handleReconnectionEnd(message);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Handle storage event (localStorage fallback)
   * NOTE: storage event won't fire in the SAME tab (cross-tab only)
   * This is correct - we only use it for cross-tab communication
   */
  private handleStorageEvent(e: StorageEvent): void {
    // Guard: only listen to our specific key
    if (e.key !== SYNC_FALLBACK_KEY) return;

    try {
      const message: SyncBroadcast = JSON.parse(e.newValue || '{}');
      this.handleMessage(message);
    } catch {
      // Silently discard malformed payloads (avoid log spam)
      return;
    }
  }

  /**
   * Handle record-changed message
   */
  private handleRecordChanged(message: SyncBroadcast & { type: 'record-changed' }): void {
    const { table, recordId, section, opId } = message;

    // De-duplication: skip if we've already seen this opId
    if (opId && this.seenOpIds.has(opId)) {
      return;
    }

    // Track this opId for 5 seconds
    if (opId) {
      this.addSeenOpId(opId);
    }

    // Coalesce refetches for this specific record
    this.coalesceRefetch(table, recordId, () => {
      // Invoke per-record subscribers
      const recordKey = `${table}:${recordId}`;
      const recordSubs = this.recordSubscribers.get(recordKey);
      if (recordSubs) {
        const payload: RecordChangePayload = section ? { section, opId } : { opId };
        recordSubs.forEach((callback) => {
          try {
            callback(payload);
          } catch (error) {
            // Log subscriber errors but don't block other subscribers
            console.error('[SyncCoordinator] Subscriber error:', error);
          }
        });
      }

      // Invoke table-wide subscribers
      const tableSubs = this.tableSubscribers.get(table);
      if (tableSubs) {
        const payload: TableChangePayload = section
          ? { recordId, section, opId }
          : { recordId, opId };
        tableSubs.forEach((callback) => {
          try {
            callback(payload);
          } catch (error) {
            // Log subscriber errors but don't block other subscribers
            console.error('[SyncCoordinator] Subscriber error:', error);
          }
        });
      }
    });
  }

  /**
   * Handle export-lock message
   */
  private handleExportLock(message: SyncBroadcast & { type: 'export-lock' }): void {
    const { state, lockId, originId } = message;

    if (state === 'grant' && originId !== this.originId) {
      // Another tab acquired the lock
      this.lockState.currentLockId = lockId;
      this.lockState.lockOwnerId = originId;
      this.lockState.lockExpiry = Date.now() + SYNC_LOCK_TTL_MS;
    } else if (state === 'release' && lockId === this.lockState.currentLockId) {
      // Lock released
      this.lockState.currentLockId = null;
      this.lockState.lockOwnerId = null;
      this.lockState.lockExpiry = null;
    }
  }

  /**
   * Handle reconnect:start message
   * Tracks active reconnections across tabs. Idempotent (safe to call multiple times).
   */
  private handleReconnectionStart(message: SyncBroadcast & { type: 'reconnect:start' }): void {
    const { provider, correlationId, ts } = message;

    // Store active reconnection for this provider
    this.activeReconnections.set(provider, {
      correlationId,
      startedAt: ts,
    });

    // Notify all reconnection subscribers
    this.reconnectionSubscribers.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        // Log subscriber errors but don't block other subscribers
        console.error('[SyncCoordinator] Reconnection subscriber error:', error);
      }
    });
  }

  /**
   * Handle reconnect:end message
   * Cleans up reconnection tracking. Idempotent.
   */
  private handleReconnectionEnd(message: SyncBroadcast & { type: 'reconnect:end' }): void {
    const { provider } = message;

    // Remove from active reconnections
    this.activeReconnections.delete(provider);

    // Notify all reconnection subscribers
    this.reconnectionSubscribers.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        // Log subscriber errors but don't block other subscribers
        console.error('[SyncCoordinator] Reconnection subscriber error:', error);
      }
    });
  }

  /**
   * Coalesce refetches with 100ms debounce per record
   * Prevents stampede from burst writes (20 writes → ~5 refetches)
   */
  private coalesceRefetch(table: string, recordId: string, callback: () => void): void {
    const key = `${table}:${recordId}`;

    // Clear existing timeout
    const existing = this.pendingRefetches.get(key);
    if (existing) {
      clearTimeout(existing);
    }

    // Schedule new timeout
    const timeout = setTimeout(() => {
      this.pendingRefetches.delete(key);
      callback();
    }, this.COALESCE_DELAY_MS);

    this.pendingRefetches.set(key, timeout);
  }

  /**
   * Add opId to seen cache with 5s expiry
   */
  private addSeenOpId(opId: string): void {
    this.seenOpIds.add(opId);

    // Schedule cleanup after 5s
    const timeout = setTimeout(() => {
      this.seenOpIds.delete(opId);
      this.opIdTimeouts.delete(opId);
    }, this.OPID_EXPIRY_MS);

    this.opIdTimeouts.set(opId, timeout);
  }

  /**
   * Broadcast message to other tabs
   */
  private broadcast(message: SyncBroadcast): void {
    if (this.usingFallback) {
      // localStorage fallback
      localStorage.setItem(SYNC_FALLBACK_KEY, JSON.stringify(message));
    } else if (this.channel) {
      // BroadcastChannel
      this.channel.postMessage(message);
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Notify other tabs of a record change
   * Auto-generates opId if not provided
   * NOTE: Does NOT notify the same tab (self-bounce guard). For same-tab updates
   * after sync import, use notifyLocalChange() instead.
   */
  notifyChange(table: string, recordId: string, section?: string, opId?: string): void {
    const message: SyncBroadcast = section
      ? {
          type: 'record-changed',
          table,
          recordId,
          section,
          opId: opId || generateUUID(),
          originId: this.originId,
          ts: Date.now(),
        }
      : {
          type: 'record-changed',
          table,
          recordId,
          opId: opId || generateUUID(),
          originId: this.originId,
          ts: Date.now(),
        };

    this.broadcast(message);
  }

  /**
   * Notify same-tab subscribers of a record change (bypasses BroadcastChannel)
   * Use this for sync import flows where the current tab needs to update its UI
   * after importing external data. Does NOT broadcast to other tabs.
   *
   * For cross-tab notification, use notifyChange() instead.
   * For sync import, call BOTH: notifyLocalChange() then notifyChange()
   */
  notifyLocalChange(table: string, recordId: string, section?: string, opId?: string): void {
    const effectiveOpId = opId || generateUUID();

    // Create a synthetic message for handleRecordChanged
    // Use same pattern as notifyChange to handle optional section properly
    const message: SyncBroadcast & { type: 'record-changed' } = section
      ? {
          type: 'record-changed',
          table,
          recordId,
          section,
          opId: effectiveOpId,
          originId: 'local', // Marker to indicate local notification
          ts: Date.now(),
        }
      : {
          type: 'record-changed',
          table,
          recordId,
          opId: effectiveOpId,
          originId: 'local', // Marker to indicate local notification
          ts: Date.now(),
        };

    // Directly invoke handleRecordChanged (bypasses self-bounce guard)
    this.handleRecordChanged(message);
  }

  /**
   * Subscribe to changes for a specific record
   * Returns unsubscribe function for cleanup
   */
  subscribeByRecord(
    table: string,
    recordId: string,
    callback: RecordChangeCallback
  ): () => void {
    const key = `${table}:${recordId}`;

    // Get or create subscriber set
    let subscribers = this.recordSubscribers.get(key);
    if (!subscribers) {
      subscribers = new Set();
      this.recordSubscribers.set(key, subscribers);
    }

    // Add callback
    subscribers.add(callback);

    // Return unsubscribe function
    return () => {
      subscribers?.delete(callback);
      if (subscribers?.size === 0) {
        this.recordSubscribers.delete(key);
      }
    };
  }

  /**
   * Subscribe to all changes for a table
   * Returns unsubscribe function for cleanup
   */
  subscribe(table: string, callback: TableChangeCallback): () => void {
    // Get or create subscriber set
    let subscribers = this.tableSubscribers.get(table);
    if (!subscribers) {
      subscribers = new Set();
      this.tableSubscribers.set(table, subscribers);
    }

    // Add callback
    subscribers.add(callback);

    // Return unsubscribe function
    return () => {
      subscribers?.delete(callback);
      if (subscribers?.size === 0) {
        this.tableSubscribers.delete(table);
      }
    };
  }

  /**
   * Subscribe to reconnection messages (reconnect:start and reconnect:end)
   *
   * Callbacks receive all reconnection messages from other tabs for cross-tab coordination.
   * Used by useProviderConnection hook to suppress duplicate connection attempts.
   *
   * @param callback - Function to invoke when reconnection messages are received
   * @returns Unsubscribe function - call to remove the subscription
   *
   * @example
   * ```ts
   * const unsubscribe = syncCoordinatorService.onReconnection((message) => {
   *   if (message.type === 'reconnect:start') {
   *     console.log(`Tab ${message.originId} started reconnecting to ${message.provider}`);
   *   }
   * });
   *
   * // Later: cleanup
   * unsubscribe();
   * ```
   *
   * Note: Callbacks must be idempotent as they may receive duplicate messages.
   * Cleanup is guaranteed via the returned unsubscribe function.
   */
  onReconnection(callback: (message: SyncBroadcast) => void): () => void {
    // Add callback to subscribers
    this.reconnectionSubscribers.add(callback);

    // Return unsubscribe function
    return () => {
      this.reconnectionSubscribers.delete(callback);
    };
  }

  /**
   * Try to acquire export lock
   * Uses capped backoff (300→600→900ms) for near-simultaneous requests
   * Returns true if lock acquired, false if another tab holds lock
   */
  async tryAcquireExportLock(): Promise<boolean> {
    // Check localStorage lock: if exists & not expired & different originId → REJECT
    const existingLockJson = localStorage.getItem(SYNC_LOCK_KEY);
    if (existingLockJson) {
      try {
        const existingLock: SyncLock = JSON.parse(existingLockJson);
        const now = Date.now();

        if (existingLock.expiresAt > now && existingLock.originId !== this.originId) {
          // Another tab holds valid lock
          return false;
        }
        // Lock expired or we already own it - continue to acquire
      } catch {
        // Malformed lock data - treat as expired
      }
    }

    // Generate new lock ID
    const lockId = generateUUID();

    // Broadcast lock request
    this.broadcast({
      type: 'export-lock',
      state: 'request',
      lockId,
      originId: this.originId,
      ts: Date.now(),
    });

    // Capped backoff wait (300→600→900ms) for grant responses
    const delays = [300, 600, 900];
    for (const delay of delays) {
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Check if another tab acquired lock
      if (
        this.lockState.lockOwnerId &&
        this.lockState.lockOwnerId !== this.originId &&
        this.lockState.lockExpiry &&
        this.lockState.lockExpiry > Date.now()
      ) {
        // Another tab holds lock
        return false;
      }
    }

    // No conflicting lock detected - acquire it
    const lock: SyncLock = {
      lockId,
      originId: this.originId,
      expiresAt: Date.now() + SYNC_LOCK_TTL_MS,
    };

    localStorage.setItem(SYNC_LOCK_KEY, JSON.stringify(lock));

    // Update local state
    this.lockState.currentLockId = lockId;
    this.lockState.lockOwnerId = this.originId;
    this.lockState.lockExpiry = lock.expiresAt;

    // Broadcast grant
    this.broadcast({
      type: 'export-lock',
      state: 'grant',
      lockId,
      originId: this.originId,
      ts: Date.now(),
    });

    return true;
  }

  /**
   * Release export lock
   * Safe to call even if we don't hold the lock (idempotent)
   */
  releaseExportLock(): void {
    // Only release if we own the lock
    if (this.lockState.lockOwnerId !== this.originId) {
      return;
    }

    const lockId = this.lockState.currentLockId;

    // Remove from localStorage
    const existingLockJson = localStorage.getItem(SYNC_LOCK_KEY);
    if (existingLockJson) {
      try {
        const existingLock: SyncLock = JSON.parse(existingLockJson);
        if (existingLock.originId === this.originId) {
          localStorage.removeItem(SYNC_LOCK_KEY);
        }
      } catch {
        // Malformed lock data - remove anyway
        localStorage.removeItem(SYNC_LOCK_KEY);
      }
    }

    // Broadcast release
    if (lockId) {
      this.broadcast({
        type: 'export-lock',
        state: 'release',
        lockId,
        originId: this.originId,
        ts: Date.now(),
      });
    }

    // Clear local state
    this.lockState.currentLockId = null;
    this.lockState.lockOwnerId = null;
    this.lockState.lockExpiry = null;
  }

  /**
   * Get BroadcastChannel instance (for direct event listener registration)
   * Returns null if BroadcastChannel is unavailable (SSR or unsupported browsers using localStorage fallback)
   */
  getChannel(): BroadcastChannel | null {
    return this.channel;
  }

  /**
   * Get this tab's unique origin ID (stable across component lifecycle)
   * Used for cross-tab coordination and self-bounce guards
   */
  getOriginId(): string {
    return this.originId;
  }

  /**
   * Cleanup on tab close
   * IDEMPOTENT: safe to call multiple times
   */
  private cleanup(): void {
    // Release export lock if we hold it
    this.releaseExportLock();

    // Close BroadcastChannel
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    // Remove storage listener
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }

    // Clear all subscriptions
    this.recordSubscribers.clear();
    this.tableSubscribers.clear();

    // Clear pending refetches
    this.pendingRefetches.forEach((timeout) => clearTimeout(timeout));
    this.pendingRefetches.clear();

    // Clear opId timeouts
    this.opIdTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.opIdTimeouts.clear();
    this.seenOpIds.clear();
  }
}

/**
 * Singleton export
 * Components and hooks import this instance directly
 */
export const syncCoordinatorService = new SyncCoordinatorService();
